import { clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import { getRegionConfig } from './regionConfig';

// tailwind-merge only knows Tailwind's own corner sizes. Told about ours
// (tailwind.config.js), a later rounded-none or rounded-t-[24px] replaces a
// component's rounded-pill instead of both being kept - when both were kept,
// pill won in the stylesheet and the override silently did nothing.
const twMerge = extendTailwindMerge({
  extend: { theme: { radius: ['small', 'cards', 'tile', 'highlight', 'pill'] } },
});

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Get region from localStorage cache
function getActiveRegion() {
  try {
    // Get from localStorage cache (set by useRegion hook and appStore)
    const cachedRegion = localStorage.getItem('user_region');
    if (cachedRegion) {
      return cachedRegion;
    }
    // Fallback to AU
    return 'AU';
  } catch {
    return 'AU';
  }
}

export function formatCurrency(amount, regionCode) {
  // If region not provided, try to get from active settings
  const region = regionCode || getActiveRegion();
  const regionConfig = getRegionConfig(region);
  
  const formatted = Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  
  const withSymbol = regionConfig.currency.position === 'before' 
    ? `${regionConfig.currency.symbol}${formatted}`
    : `${formatted}${regionConfig.currency.symbol}`;
  
  return amount < 0 ? `-${withSymbol}` : withSymbol;
}

export function formatDate(date, regionCode) {
  if (!date) return '';
  const region = regionCode || getActiveRegion();
  const regionConfig = getRegionConfig(region);
  
  return new Date(date).toLocaleDateString(regionConfig.dateFormat.locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Abbreviated rental period: "Feb 12–17" or "Feb 12 – Mar 4"
export function formatRentalPeriod(startStr, endStr) {
  if (!startStr || !endStr) return '';
  // Parse as local date to avoid timezone shift
  const [sy, sm, sd] = startStr.split('-').map(Number);
  const [ey, em, ed] = endStr.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const startMon = start.toLocaleString('en', { month: 'short' });
  const endMon = end.toLocaleString('en', { month: 'short' });
  if (sm === em && sy === ey) {
    return `${startMon} ${sd}–${ed}`;
  }
  return `${startMon} ${sd} – ${endMon} ${ed}`;
}

export function formatDateDMY(date, regionCode) {
  if (!date) return '';
  const region = regionCode || getActiveRegion();
  const regionConfig = getRegionConfig(region);
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  
  // Format based on region
  if (region === 'US') {
    return `${month}-${day}-${year}`; // MM-DD-YYYY for US
  }
  return `${day}-${month}-${year}`; // DD-MM-YYYY for others
}

export function calculateROI(totalIncome, purchasePrice, disposedValue = 0) {
  if (purchasePrice <= 0) return 0;
  // ROI = (Total Income + Disposed Value - Purchase Price) / Purchase Price * 100
  return ((totalIncome + disposedValue - purchasePrice) / purchasePrice) * 100;
}

// Status pills are told apart by fill as well as colour: outlined draft, mist
// sent, inverse booked, tinted completed and lost.
export function getStatusColor(status) {
  const colors = {
    draft: 'border border-dashed border-line-strong bg-transparent text-muted-foreground',
    sent: 'bg-mist text-foreground',
    quoted: 'bg-mist text-foreground', // Keep for backwards compatibility
    booked: 'bg-inverse text-inverse-foreground',
    lost: 'bg-brick-soft text-brick',
    in_progress: 'bg-ochre-soft text-ochre', // Keep for backwards compatibility
    completed: 'bg-moss-soft text-moss',
    invoiced: 'border border-foreground bg-transparent text-foreground',
    paid: 'bg-moss-soft text-moss',
  };
  return colors[status] || colors.draft;
}

export function getROIStatus(roi) {
  if (roi >= 100) return { label: 'Profitable', color: 'text-success', icon: 'success' };
  if (roi >= 0) return { label: 'Approaching Breakeven', color: 'text-warning', icon: 'warning' };
  return { label: 'Not Profitable Yet', color: 'text-destructive', icon: 'error' };
}

export function formatABN(abn) {
  if (!abn) return '';
  const cleaned = abn.replace(/\s/g, '');
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4');
}

